import React, { useEffect, useState } from 'react';
import { Button, IconButton, Tooltip, makeStyles } from '@material-ui/core';
import { ExpandLess, ExpandMore, WarningOutlined } from '@material-ui/icons';
import { ActivityScanProgress, ActivityStopReason, ScanStatus } from './types';

const ACCENT = '#5b4bc4';
const GREEN = '#1f7a4d';
const SLATE = '#6b7280';

const useStyles = makeStyles({
  panel: {
    marginBottom: 20,
    background: '#ffffff',
    border: '1px solid #dde1e6',
    borderRadius: 10,
    overflow: 'hidden',
    color: '#1b1f24',
    fontFamily: "Lato, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    lineHeight: 1.4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '14px',
    minHeight: 56,
    padding: '7px 10px 7px 18px',
    boxSizing: 'border-box',
  },
  icon: { flexShrink: 0 },
  spinner: { animation: '$scanSpin 1s linear infinite', transformOrigin: '50% 50%' },
  title: { fontWeight: 700, fontSize: 15 },
  matches: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  track: { width: 140, height: 6, borderRadius: 3, background: '#e9ecef', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3 },
  matchText: {
    fontSize: 13,
    color: '#4b5563',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  divider: { width: 1, height: 20, background: '#e3e6ea', flexShrink: 0 },
  summary: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 120px',
    minWidth: 0,
    gap: '10px',
    fontSize: 13,
    color: '#5f6368',
  },
  summaryText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: 999,
    background: '#fff4e5',
    color: '#9a4b00',
    fontWeight: 700,
    minWidth: 0,
    maxWidth: '100%',
    '& svg': { fontSize: 14, flexShrink: 0 },
  },
  elapsed: {
    fontSize: 13,
    color: '#5f6368',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  action: {
    height: 40,
    padding: '0 14px',
    border: '1px solid #d0d5db',
    borderRadius: 8,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#1b1f24',
    textTransform: 'none',
    flexShrink: 0,
  },
  toggle: { width: 40, height: 40, borderRadius: 8, color: '#5f6368', '& svg': { fontSize: 18 } },
  details: {
    borderTop: '1px solid #e3e6ea',
    background: '#fafbfc',
    padding: '18px 18px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  columns: { display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' },
  column: { display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 },
  workColumn: { flex: '1 1 420px' },
  groupsColumn: { flex: '0 1 250px' },
  serverColumn: { flex: '0 1 230px' },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: '#5f6368',
  },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px' },
  card: {
    padding: '12px 14px',
    background: '#ffffff',
    border: '1px solid #e3e6ea',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  value: {
    fontSize: 22,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
  },
  note: { fontSize: 12, color: '#5f6368', lineHeight: 1.5 },
  noStats: {
    borderStyle: 'dashed',
    borderColor: '#d0d5db',
    padding: 14,
    fontSize: 13,
    color: '#5f6368',
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    background: '#eceff2',
    border: '1px solid #e3e6ea',
    borderRadius: 8,
    overflow: 'hidden',
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 14px',
    background: '#ffffff',
  },
  severity: {
    width: 62,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.04em',
    color: '#374151',
  },
  dot: {
    width: 9,
    height: 9,
    flexShrink: 0,
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: '2px solid',
  },
  pulse: { animation: '$scanPulse 1.6s ease-in-out infinite' },
  groupState: { fontSize: 13 },
  serverValue: {
    fontSize: 16,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  '@keyframes scanSpin': { to: { transform: 'rotate(360deg)' } },
  '@keyframes scanPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
  '@media (max-width: 1100px)': {
    header: { gap: '10px' },
    summary: { order: 1, flexBasis: '100%', paddingBottom: 4, flexWrap: 'wrap' },
    divider: { display: 'none' },
    controls: { gap: '10px' },
    summaryText: { whiteSpace: 'normal', overflowWrap: 'anywhere' },
  },
  '@media (max-width: 600px)': {
    header: { gap: '8px' },
    matches: { gap: '8px' },
    track: { width: 60 },
    controls: { gap: '8px' },
    toggle: { width: 32, padding: 6 },
    stats: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    groupsColumn: { flexBasis: '100%' },
    serverColumn: { flexBasis: '100%' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    spinner: { animation: 'none' },
    pulse: { animation: 'none' },
  },
});

interface Props {
  status: ScanStatus;
  requiresRefresh: boolean;
  loaded: number;
  target: number;
  elapsedMs: number;
  fetchedAt: number | null;
  requestStartedAt: number | null;
  retryAttempt: number;
  stopReason: ActivityStopReason | null;
  progress: ActivityScanProgress | null;
  rangeDays: number;
  severity: string;
  onCancel: () => void;
  onResume: () => void;
  onRefresh: () => void;
}

const duration = (seconds: number) =>
  seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const ActivityScanStatus = ({
  status,
  requiresRefresh,
  loaded,
  target,
  elapsedMs,
  fetchedAt,
  requestStartedAt,
  retryAttempt,
  stopReason,
  progress,
  rangeDays,
  severity,
  onCancel,
  onResume,
  onRefresh,
}: Props) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(false);
  const finished = status === 'full' || status === 'complete';

  useEffect(() => {
    setHidden(false);
    if (!finished) return;
    const timer = window.setTimeout(() => setHidden(true), 5000);
    return () => window.clearTimeout(timer);
  }, [finished, fetchedAt]);

  const busy = status === 'loading';
  const now = Date.now();
  const delayed = busy && requestStartedAt !== null && now - requestStartedAt >= 20000;
  const heading = busy
    ? 'Searching activity logs…'
    : status === 'paused'
      ? 'Search paused'
      : status === 'error'
        ? 'Search interrupted'
        : status === 'complete'
          ? 'Search range exhausted'
          : 'Page filled';
  const color = busy ? ACCENT : status === 'full' ? GREEN : status === 'error' ? '#9a4b00' : SLATE;
  const fillPct = target > 0 ? Math.max(0, Math.min(100, (loaded / target) * 100)) : 0;
  const lastResponse =
    fetchedAt === null
      ? 'None yet'
      : busy
        ? `${duration(Math.max(0, Math.floor((now - fetchedAt) / 1000)))} ago`
        : `${new Date(fetchedAt).toISOString().slice(11, 19)} UTC`;
  const warning =
    busy && retryAttempt
      ? `Retrying search · attempt ${retryAttempt} of 3`
      : delayed
        ? 'Waiting for server response…'
        : busy && stopReason === 'AWS_TIMEOUT'
          ? 'CloudWatch responded slowly · retrying automatically'
          : '';
  const lastNote =
    busy && retryAttempt
      ? 'Retrying from the saved position; no new response confirmed yet.'
      : delayed
        ? 'This request is taking longer than usual; progress is unconfirmed while waiting.'
        : busy && stopReason === 'AWS_TIMEOUT'
          ? 'CloudWatch responded slowly; retrying automatically from the saved position.'
          : requiresRefresh
            ? 'Review the filters and refresh to start a new search.'
            : status === 'paused'
              ? 'Search paused. Resume to continue from the saved position.'
              : status === 'error'
                ? 'Search interrupted. Retry to continue from the saved position.'
                : fetchedAt === null
                  ? 'No server response confirmed'
                  : 'Confirmed';
  const searchingGroups = progress
    ? Object.keys(progress.groups).filter(
        (name) => !progress.groups[name].exhausted && !progress.groups[name].candidateReady
      )
    : [];
  const summary =
    fetchedAt === null
      ? busy
        ? `Waiting for the first server response.${rangeDays >= 30 ? ' Wide date ranges can take longer, even before the first match.' : ''}`
        : `No server response confirmed. ${lastNote}`
      : `Last response ${lastResponse}${
          status === 'complete'
            ? ' · no more logs in range'
            : status === 'full' && searchingGroups.length
              ? ` · ${searchingGroups.join(', ')} ${searchingGroups.length === 1 ? 'group' : 'groups'} still searching`
              : status === 'paused' || status === 'error'
                ? ` · ${lastNote}`
                : ''
        }`;
  const stats = progress
    ? [
        { label: 'Batches read', value: progress.pagesFetched },
        { label: 'Empty batches', value: progress.emptyPages },
        { label: 'Replayed', value: progress.replayedPages },
        { label: 'Candidate checks', value: progress.candidateChecks },
      ]
    : [];
  const severities = ['HIGH', 'MEDIUM', 'LOW'].filter(
    (name) => severity === 'ALL' || severity === name
  );

  if (finished && hidden) return null;

  return (
    <section className={classes.panel} aria-label="Search progress">
      <div className={classes.header}>
        <svg
          className={`${classes.icon} ${busy ? classes.spinner : ''}`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true">
          {busy ? (
            <path d="M12 3a9 9 0 1 0 9 9" />
          ) : (
            <>
              <circle cx="12" cy="12" r="9" />
              {status === 'full' ? (
                <path d="m8.5 12.5 2.5 2.5 5-5.5" />
              ) : status === 'error' ? (
                <path d="M12 7v6m0 4v.5" />
              ) : (
                <path d="M9 9h6v6H9z" />
              )}
            </>
          )}
        </svg>
        <span className={classes.title} role="status" aria-live="polite">
          {heading}
        </span>
        <div className={classes.matches}>
          <Tooltip title="Matches loaded toward the page target, not search completion">
            <div
              className={classes.track}
              role="progressbar"
              aria-label="Matches loaded toward page target"
              aria-valuemin={0}
              aria-valuemax={target}
              aria-valuenow={Math.min(loaded, target)}
              aria-valuetext={`${loaded} of ${target} matches loaded; not search completion`}>
              <div className={classes.fill} style={{ width: `${fillPct}%`, background: color }} />
            </div>
          </Tooltip>
          <span className={classes.matchText}>
            {loaded} / {target} matches
          </span>
        </div>
        <div className={classes.divider} aria-hidden="true" />
        <div className={classes.summary}>
          {warning && (
            <Tooltip title={warning}>
              <div className={classes.warning} role="status" aria-live="polite">
                <WarningOutlined aria-hidden="true" />
                <span className={classes.summaryText}>{warning}</span>
              </div>
            </Tooltip>
          )}
          <span className={classes.summaryText} title={summary}>
            {summary}
          </span>
        </div>
        <div className={classes.controls}>
          <span
            className={classes.elapsed}
            aria-label={`${duration(Math.floor(elapsedMs / 1000))} elapsed`}>
            {duration(Math.floor(elapsedMs / 1000))}
          </span>
          {busy ? (
            <Button className={classes.action} variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            (status === 'paused' || status === 'error') && (
              <Button
                className={classes.action}
                variant="outlined"
                onClick={requiresRefresh ? onRefresh : onResume}>
                {requiresRefresh ? 'Refresh search' : status === 'error' ? 'Retry' : 'Resume'}
              </Button>
            )
          )}
          <IconButton
            className={classes.toggle}
            aria-label={expanded ? 'Hide search details' : 'Show search details'}
            aria-expanded={expanded}
            aria-controls="activity-scan-details"
            onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
      </div>
      {expanded && (
        <div className={classes.details} id="activity-scan-details">
          <div className={classes.columns}>
            <div className={`${classes.column} ${classes.workColumn}`}>
              <div className={classes.label}>CloudWatch</div>
              {progress ? (
                <div className={classes.stats}>
                  {stats.map((stat) => (
                    <div className={classes.card} key={stat.label}>
                      <div className={classes.value}>{stat.value.toLocaleString()}</div>
                      <div className={classes.note}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`${classes.card} ${classes.noStats}`}>
                  {fetchedAt !== null
                    ? 'Search responses received; this server does not provide work counters.'
                    : 'No batches confirmed yet. Counts appear after the first confirmed server update.'}
                </div>
              )}
            </div>
            <div className={`${classes.column} ${classes.groupsColumn}`}>
              <div className={classes.label}>Severity groups</div>
              <div className={classes.groups}>
                {severities.map((name) => {
                  const group = progress && progress.groups[name];
                  const state = !group
                    ? fetchedAt === null
                      ? 'Pending'
                      : 'Unavailable'
                    : group.exhausted
                      ? 'Exhausted'
                      : group.candidateReady
                        ? 'Candidate ready'
                        : 'Searching';
                  const dotColor = !group
                    ? '#e3e6ea'
                    : group.exhausted
                      ? '#9aa0a6'
                      : group.candidateReady
                        ? GREEN
                        : ACCENT;
                  return (
                    <Tooltip
                      key={name}
                      title={
                        group
                          ? `${group.pagesFetched} batches read, ${group.emptyPages} empty, ${group.replayedPages} replayed. Last confirmed state.`
                          : fetchedAt === null
                            ? 'Waiting for the first confirmed group update.'
                            : 'This server did not return a group state.'
                      }>
                      <div className={classes.group}>
                        <span className={classes.severity}>{name}</span>
                        <span
                          className={`${classes.dot} ${busy && group && !group.exhausted && !group.candidateReady ? classes.pulse : ''}`}
                          style={{
                            background: group && group.exhausted ? 'transparent' : dotColor,
                            borderColor: dotColor,
                          }}
                          aria-hidden="true"
                        />
                        <span className={classes.groupState}>{state}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
            <div className={`${classes.column} ${classes.serverColumn}`}>
              <div className={classes.label}>Server</div>
              <div className={classes.card}>
                <div className={classes.note}>Last confirmed response</div>
                <div className={classes.serverValue}>{lastResponse}</div>
                <div className={classes.note}>{lastNote}</div>
              </div>
            </div>
          </div>
          <div className={classes.note}>
            Batch and candidate-check counts include replay work, not unique events. Group states
            are from the last server response; a ready candidate may be waiting for other groups.
          </div>
        </div>
      )}
    </section>
  );
};

export default ActivityScanStatus;
